// src/components/Configuraciones/Respaldo.jsx
import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/Api";
import { useAuth } from "../../context/AuthContext";
import { isSuperAdmin } from "../../services/authUtils";

const fmtBytes = (bytes) => {
  const b = Number(bytes || 0);
  if (!b) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / Math.pow(1024, i)).toFixed(2)} ${units[i]}`;
};

const mapBackupItem = (raw) => {
  // Acepta distintos nombres de propiedades para ser tolerantes
  const fileName = raw?.fileName ?? raw?.name ?? raw?.FileName ?? raw?.Name ?? "";
  const size = raw?.size ?? raw?.length ?? raw?.Size ?? raw?.Length ?? 0;
  const createdUtc =
    raw?.createdUtc ?? raw?.creationTimeUtc ?? raw?.CreatedUtc ?? raw?.CreationTimeUtc ?? null;

  return {
    fileName,
    size,
    createdUtc,
  };
};

const Respaldo = () => {
  const { user } = useAuth();
  const esSuper = useMemo(() => !!isSuperAdmin() || !!user?.superUsuario, [user?.superUsuario]);

  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [accionBusy, setAccionBusy] = useState(false);
  const [autoRefreshMs, setAutoRefreshMs] = useState(() => {
    const k = "cfg-respaldo:autoRefresh";
    const saved = Number(window.localStorage.getItem(k));
    return Number.isFinite(saved) ? saved : 0;
  });

  useEffect(() => {
    window.localStorage.setItem("cfg-respaldo:autoRefresh", String(autoRefreshMs));
  }, [autoRefreshMs]);

  const fetchList = async () => {
    setCargando(true);
    setError("");
    try {
      const { data } = await api.get("/backup/list");
      const list = Array.isArray(data) ? data.map(mapBackupItem) : [];
      setItems(list);
    } catch (e) {
      console.error(e);
      setError("No se pudo cargar la lista de respaldos.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (!esSuper) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esSuper]);

  useEffect(() => {
    if (!autoRefreshMs || autoRefreshMs <= 0) return;
    const id = setInterval(fetchList, autoRefreshMs);
    return () => clearInterval(id);
  }, [autoRefreshMs]);

  const crearBackup = async () => {
    if (!esSuper || accionBusy) return;
    if (!window.confirm("Se creará un respaldo completo de la base de datos. ¿Continuar?")) return;
    setAccionBusy(true);
    setError("");
    try {
      const { data } = await api.post("/backup/create");
      await fetchList();
      alert(`Respaldo creado: ${data?.fileName ?? "(desconocido)"}`);
    } catch (e) {
      console.error(e);
      setError("No se pudo crear el respaldo.");
    } finally {
      setAccionBusy(false);
    }
  };

  const restaurar = async (fileName) => {
    if (!esSuper || accionBusy) return;
    if (!fileName) return;
    if (
      !window.confirm(
        `Se restaurará la base de datos desde:\n\n${fileName}\n\nEsto sobrescribe la BD actual. ¿Continuar?`
      )
    )
      return;

    setAccionBusy(true);
    setError("");
    try {
      const confirmation = window.prompt(`Escribe RESTAURAR ${fileName} para confirmar el reemplazo de los datos actuales.`);
      if (confirmation !== `RESTAURAR ${fileName}`) return;
      await api.post("/backup/restore", { fileName, confirmation });
      alert("Base de datos restaurada desde el respaldo seleccionado.");
    } catch (e) {
      console.error(e);
      setError("No se pudo restaurar el respaldo.");
    } finally {
      setAccionBusy(false);
    }
  };

  const eliminar = async (fileName) => {
    if (!esSuper || accionBusy) return;
    if (!fileName) return;
    if (!window.confirm(`Eliminar el respaldo\n\n${fileName}\n\n¿Seguro?`)) return;

    setAccionBusy(true);
    setError("");
    try {
      await api.delete(`/backup/${encodeURIComponent(fileName)}`);
      await fetchList();
    } catch (e) {
      console.error(e);
      setError("No se pudo eliminar el respaldo.");
    } finally {
      setAccionBusy(false);
    }
  };

  if (!esSuper) {
    return (
      <div className="min-h-[40dvh] bg-neutral-950 text-white flex items-center justify-center">
        <div className="bg-neutral-900/70 border border-neutral-700 rounded-lg p-4 text-center max-w-lg">
          <div className="text-lg font-semibold text-red-300 mb-1">Acceso restringido</div>
          <div className="text-sm text-neutral-300">
            Esta sección de <span className="font-semibold">Respaldo</span> está disponible solo
            para superadministradores.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-neutral-900 text-white rounded-xl p-4 sm:p-6 border border-emerald-700/30">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-xl sm:text-2xl font-bold text-emerald-400">Respaldo de Base de Datos</h2>
        <div className="flex items-center gap-2">
          <label className="text-xs sm:text-sm flex items-center gap-2">
            <span className="text-neutral-300">Auto-refrescar:</span>
            <select
              value={autoRefreshMs}
              onChange={(e) => setAutoRefreshMs(Number(e.target.value))}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs sm:text-sm"
              title="Frecuencia de actualización automática"
            >
              <option value={0}>Apagado</option>
              <option value={30_000}>30 s</option>
              <option value={60_000}>1 min</option>
              <option value={120_000}>2 min</option>
              <option value={300_000}>5 min</option>
            </select>
          </label>
          <button
            onClick={fetchList}
            className="bg-slate-800 hover:bg-slate-700 border border-slate-600 text-white px-3 py-1 rounded text-sm"
            disabled={cargando || accionBusy}
          >
            Refrescar
          </button>
          <button
            onClick={crearBackup}
            className="bg-emerald-700 hover:bg-emerald-600 text-white px-3 py-1 rounded text-sm"
            disabled={accionBusy}
            title="Crear un nuevo archivo .bak con fecha"
          >
            Crear respaldo
          </button>
        </div>
      </div>

      {cargando && <div className="mt-4 text-neutral-300">Cargando…</div>}
      {!cargando && error && (
        <div className="mt-4 bg-red-900/30 border border-red-700 text-red-200 px-3 py-2 rounded">
          {error}
        </div>
      )}

      <div className="mt-4 overflow-x-auto rounded border border-slate-800">
        <table className="min-w-[720px] w-full text-sm">
          <thead>
            <tr className="bg-slate-800 text-cyan-200">
              <th className="px-2 py-2 text-left">Archivo</th>
              <th className="px-2 py-2 text-left">Fecha (UTC)</th>
              <th className="px-2 py-2 text-right">Tamaño</th>
              <th className="px-2 py-2 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {!cargando && items.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-center text-neutral-400">
                  No hay respaldos aún.
                </td>
              </tr>
            )}
            {items.map((it) => (
              <tr key={it.fileName} className="odd:bg-slate-900 even:bg-slate-950">
                <td className="px-2 py-2 font-mono break-all">{it.fileName}</td>
                <td className="px-2 py-2">
                  {it.createdUtc ? new Date(it.createdUtc).toISOString() : "—"}
                </td>
                <td className="px-2 py-2 text-right">{fmtBytes(it.size)}</td>
                <td className="px-2 py-2">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => restaurar(it.fileName)}
                      className="bg-indigo-700 hover:bg-indigo-600 text-white px-3 py-1 rounded text-xs"
                      disabled={accionBusy}
                      title="Restaurar BD desde este respaldo"
                    >
                      Restaurar
                    </button>
                    <button
                      onClick={() => eliminar(it.fileName)}
                      className="bg-red-700 hover:bg-red-600 text-white px-3 py-1 rounded text-xs"
                      disabled={accionBusy}
                      title="Eliminar respaldo"
                    >
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-neutral-400">
        Los archivos se guardan en la ruta configurada en <code>Backup:Folder</code> y llevan el
        nombre <code>BaseDeDatos_YYYYMMDD_HHMMSS.bak</code>.
      </p>
    </div>
  );
};

export default Respaldo;
